import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import LanguageSwitcher from "./LanguageSwitcher"
import { useLanguage } from "../i18n/LanguageContext"
import {
  LogOut,
  Users,
  Truck,
  ShoppingCart,
  DollarSign,
  Home,
  ChevronLeft,
  Settings,
  MapPinned,
  Bell,
  ReceiptText,
  Menu,
  X,
  Activity,
  ShieldAlert,
  ClipboardList,
  Tags
} from "lucide-react"

export default function Layout({ children, adminInfo, onLogout }) {
  const { t } = useLanguage()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = () => {
    onLogout()
    navigate("/login")
  }

  const closeMobileSidebar = () => setMobileSidebarOpen(false)

  const menuItems = [
    { path: "/dashboard", label: t("nav.dashboard"), icon: Home },
    { path: "/captains", label: t("nav.captains"), icon: Truck },
    { path: "/captain-controls", label: t("nav.captainControls"), icon: Settings },
    { path: "/users", label: t("nav.users"), icon: Users },
    { path: "/bookings", label: t("nav.bookings"), icon: ShoppingCart },
    { path: "/financials", label: t("nav.financials"), icon: DollarSign },
    { path: "/notifications", label: t("nav.notifications"), icon: Bell },
    { path: "/receipts", label: t("nav.receipts"), icon: ReceiptText },
    { path: "/operations", label: t("nav.operationalControl"), icon: Activity },
    { path: "/exceptions", label: t("nav.exceptions"), icon: ShieldAlert },
    { path: "/audit-log", label: t("nav.auditLog"), icon: ClipboardList },
    { path: "/captain-pricing", label: t("nav.captainPricing"), icon: Tags },
    { path: "/live-map", label: t("nav.liveMap"), icon: MapPinned, external: true }
  ]

  const isActive = (path) => location.pathname === path

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Sidebar */}
      {mobileSidebarOpen && <button
        type="button"
        aria-label={t("nav.closeMenu")}
        className="mobile-menu-backdrop"
        onClick={closeMobileSidebar}
      />}
      <aside
        className={`admin-sidebar ${
          sidebarOpen ? "sidebar-expanded" : "sidebar-collapsed"
        } ${mobileSidebarOpen ? "mobile-sidebar-open" : ""} flex-shrink-0 bg-white border-r border-gray-200 shadow-xl transition-all duration-300 ease-in-out flex flex-col relative`}>
        {/* Logo & Toggle */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-gray-100">
          {sidebarOpen && (
            <div className="admin-brand flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <div className="admin-brand-text">
                <h2 className="font-bold text-lg text-gray-900">Tout's Routes</h2>
                <p className="text-xs text-gray-500">{t("auth.adminDashboard")}</p>
              </div>
            </div>
          )}
          {!sidebarOpen && (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg mx-auto">
              <Truck className="w-5 h-5 text-white" />
            </div>
          )}
        </div>

        {/* Toggle Button - Floating */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? t("nav.collapseSidebar") : t("nav.expandSidebar")}
          title={sidebarOpen ? t("nav.collapseSidebar") : t("nav.expandSidebar")}
          className={`sidebar-toggle ${!sidebarOpen ? "sidebar-toggle-collapsed" : ""} absolute -right-3 top-24 w-6 h-6 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center hover:bg-amber-50 hover:border-amber-300 transition-all shadow-md z-10`}>
          <ChevronLeft
            className={`w-4 h-4 text-gray-600 transition-transform duration-300 ${
              !sidebarOpen && "rotate-180"
            }`}
          />
        </button>

        {/* Navigation Menu */}
        <nav className="flex-1 py-8 px-3 space-y-1 overflow-y-auto overflow-x-hidden">
          {menuItems.map((item) => {
            const Icon = item.icon
            const active = !item.external && isActive(item.path)
            return (
              <a
                key={item.path}
                href={item.path}
                style={{ textDecoration: "none" }}
                onClick={closeMobileSidebar}
                className={`group flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 relative no-underline ${
                  active
                    ? "bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/30"
                    : "text-gray-700 hover:bg-gray-50 hover:text-amber-600"
                }`}>
                {active && <div className="active-navigation-indicator absolute left-0 w-1 h-8 bg-white rounded-r-full" />}
                <Icon
                  className={`w-5 h-5 flex-shrink-0 ${
                    active ? "text-white" : "group-hover:scale-110 transition-transform"
                  }`}
                />
                {sidebarOpen && (
                  <span className={`admin-menu-text font-medium ${active ? "text-white" : ""}`}>{item.label}</span>
                )}
                {!sidebarOpen && (
                  <div className="sidebar-tooltip absolute left-full ml-6 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 shadow-xl">
                    {item.label}
                    <div className="sidebar-tooltip-arrow absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                  </div>
                )}
              </a>
            )
          })}
        </nav>

        {/* Admin Info & Logout */}
        <div className="border-t border-gray-100 p-4 space-y-3">
          {sidebarOpen && (
            <div className="admin-profile p-3 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                  {adminInfo?.name?.charAt(0) || "A"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                  {adminInfo?.name || t("common.admin")}
                  </p>
                  <p className="text-xs text-gray-600 truncate">{adminInfo?.email}</p>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleLogout}
            className={`group flex items-center gap-3 w-full px-4 py-3.5 rounded-xl bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium ${
              !sidebarOpen && "justify-center"
            }`}>
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>{t("nav.logout")}</span>}
            {!sidebarOpen && (
              <div className="sidebar-tooltip absolute left-full ml-6 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 shadow-xl">
                {t("nav.logout")}
                <div className="sidebar-tooltip-arrow absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="admin-header bg-white/80 backdrop-blur-sm border-b border-gray-200 shadow-sm">
          <div className="admin-header-inner h-full flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                aria-label={mobileSidebarOpen ? t("nav.closeMenu") : t("nav.openMenu")}
                title={mobileSidebarOpen ? t("nav.closeMenu") : t("nav.openMenu")}
                className="mobile-menu-button p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileSidebarOpen((open) => !open)}
              >
                {mobileSidebarOpen ? <X className="w-5 h-5 text-gray-700" /> : <Menu className="w-5 h-5 text-gray-700" />}
              </button>
              <div className="min-w-0">
              <h1 className="admin-page-title font-bold truncate bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                {menuItems.find((item) => item.path === location.pathname)?.label || t("nav.dashboard")}
              </h1>
              <p className="admin-welcome text-sm text-gray-500 mt-0.5">
                {t("nav.welcomeBack", { name: adminInfo?.name?.split(" ")[0] || t("common.admin") })}
              </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <LanguageSwitcher compact className="admin-language-switcher" />
              <button aria-label={t("nav.settings")} title={t("nav.settings")} className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors">
                <Settings className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="admin-page-content flex-1 min-w-0 overflow-x-auto overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="w-full min-w-0 max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  )
}
